import {useQueryClient} from '@tanstack/react-query';
import {signOut} from 'aws-amplify/auth';
import {ConsoleLogger, Hub} from 'aws-amplify/utils';
import {useSnackbar} from 'notistack';
import {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {onSessionExpired} from '../../services/sessionExpiry';

const logger = new ConsoleLogger('useSessionExpiry');

/**
 * Les erreurs de renouvellement qu'Amplify tient pour DÉFINITIVES — celles sur lesquelles il
 * vide lui-même ses jetons (`TokenOrchestrator.isAuthenticationError`). `tokenRefresh_failure`
 * part aussi sur une coupure réseau ou une indisponibilité passagère de Cognito, après
 * lesquelles les jetons restent valables : déconnecter l'utilisateur là-dessus, c'est lui
 * faire perdre son brouillon pour un Wi-Fi qui a toussé.
 */
const DEFINITIVE_REFRESH_ERRORS = [
  'NotAuthorizedException',
  'TokenRevokedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'RefreshTokenReuseException',
];

function isDefinitiveRefreshError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    // Pas de détail : on ne peut pas savoir, et une session sans jeton ne mène nulle part.
    return true;
  }
  return DEFINITIVE_REFRESH_ERRORS.some((name) => error.name.startsWith(name));
}

/**
 * Rend visible la fin d'une session, d'où qu'elle vienne :
 *
 * - `tokenRefresh_failure` sur le `Hub` d'Amplify : le jeton de rafraîchissement est mort,
 *   Amplify a vidé ses jetons et ne le dit qu'ici (l'unique écouteur de cet événement) ;
 * - `notifySessionExpired()` du client de l'API : la passerelle a refusé une requête APRÈS
 *   renouvellement des identifiants (cf. `SessionAwareClient`).
 *
 * Dans les deux cas : le message `apiError.unauthorized` (« Votre session a expiré… »), puis
 * `signOut()` et retour à `/signin` avec la page courante dans `state.from`, comme le fait
 * déjà `LoginRequiredRoute` au montage — l'utilisateur revient sur son brouillon une fois
 * reconnecté. Une seule réaction par montage, même si dix requêtes tombent en rafale :
 * `preventDuplicate` pour le snackbar, un verrou pour le reste.
 *
 * Avant : les deux écouteurs du `Hub` (`App`, `LoginRequiredRoute`) se contentaient d'un
 * `console.log`, et le 403 de la passerelle s'affichait comme « Votre compte ne permet pas
 * cette action ».
 */
export function useSessionExpiry(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();

  // Lue au moment de l'expiration, pas à l'abonnement : l'utilisateur a navigué entre-temps.
  const locationRef = useRef(location);
  locationRef.current = location;
  const endedRef = useRef(false);

  useEffect(() => {
    const endSession = () => {
      if (endedRef.current) {
        return;
      }
      endedRef.current = true;
      enqueueSnackbar(t('apiError.unauthorized'), {variant: 'error', preventDuplicate: true});
      const from = locationRef.current.pathname + locationRef.current.search;
      void signOut()
        .catch((error: unknown) => {
          // Jetons déjà vidés, réseau coupé… : on repart à la connexion quand même.
          logger.warn('signOut a échoué après expiration de la session', error);
        })
        .then(() => {
          // Même nettoyage que `useLogoutMutation` : le cache détient l'utilisateur et ses données.
          queryClient.clear();
          navigate('/signin', {state: {from}});
        });
    };

    const unsubscribeHub = Hub.listen('auth', ({payload}) => {
      if (payload.event === 'tokenRefresh_failure' && isDefinitiveRefreshError(payload.data?.error)) {
        endSession();
      }
    });
    const unsubscribeClient = onSessionExpired(endSession);
    return () => {
      unsubscribeHub();
      unsubscribeClient();
    };
  }, [enqueueSnackbar, navigate, queryClient, t]);
}
